import React, { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { getStepDecimals } from '@pages/Trade/utils/helpers'

import { BaseModalType } from '@context/ModalsContext'
import { useSnackbar } from '@context/SnackbarContext'

import { SensitiveData } from '@ui/Privacy/SensitiveData'
import { Body1, Caption1 } from '@ui/Typography'
import { Modal } from '@ui/components/Modal'

import { ProtectiveOrderTypes } from '@model/DxTrade.types'
import type { DxTradeOrderType, ProtectiveOrderType } from '@model/DxTrade.types'
import { CurrencyType } from '@model/WalletDto'

import { useFormatNumber } from '@hooks/useFormatNumber'

import { Box } from '@shared/components/Box'

import { FIX_POSITION_EFFECT, FIX_TIME_IN_FORCE } from '../../utils/constants'
import { mapOrderTypeToFixOrdType } from '../../utils/helpers'
import { LivePriceCell } from '../components/LivePriceCell'
import { ProtectiveOrder } from '../components/ProtectiveOrder/ProtectiveOrder'
import { TradeCardRow } from '../components/TradeCardRow'
import { TradePnL } from '../components/TradePnL'
import { useCurrentPriceForTrade } from '../hooks/useCurrentPricesStore'
import type { PortfolioTrade } from '../hooks/useDxtradePortfolioTrades'
import type {
  ChangePendingOrderSlTpParams,
  NewOrderListOrderParams,
  NewOrderParams,
  OrderCancelParams,
  OrderCancelReplaceParams,
} from '../hooks/useFixTradingWebSocket'
import { FixContingencyTypes } from '../hooks/useFixTradingWebSocket'
import type { PendingOrderWithLots } from '../hooks/usePendingOrdersWithLots'
import { formatSymbol, getOpenLegTargetPrice, renderSide } from '../utils/formatters'
import { invertSide, sideMapping } from '../utils/mapping'
import {
  formatPriceForInstrument,
  getDefaultProtectivePrice,
  getProtectiveOrderValidation,
  isNumber,
} from '../utils/tpslHelpers'

import cardStyles from '../components/CardTradingView.module.scss'

interface CloseTradeModalProps {
  order?: PendingOrderWithLots
  position?: PortfolioTrade
  accountCode?: string
  accountCurrency?: CurrencyType
  instrumentCurrency?: CurrencyType
  instrumentMeta: { pipSize?: number | null; priceIncrement?: number | null }
  initialActiveType?: ProtectiveOrderType
  onTPSLChange?: () => void
  cancelOrder?: (params: OrderCancelParams) => string
  newOrder?: (params: NewOrderParams, onSuccess?: () => void, onSettled?: () => void) => string
  changePendingOrderSlTp?: (
    params: ChangePendingOrderSlTpParams,
    onSuccess?: () => void,
    onSettled?: () => void
  ) => string
  replaceOrder?: (
    params: OrderCancelReplaceParams,
    onSuccess?: () => void,
    onSettled?: () => void
  ) => string
}

type ProtectiveOrderState = {
  price: number | ''
  isActive: boolean
}

const getProtectiveOrdType = (kind: 'takeProfit' | 'stopLoss'): '2' | '3' =>
  kind === 'takeProfit' ? '2' : '3'

const createInitialOrderState = (
  initialPrice?: number | null,
  isActiveByDefault = false
): ProtectiveOrderState => ({
  price: initialPrice ?? '',
  isActive: isActiveByDefault || initialPrice != null,
})

const createPriceChangeHandler = (
  setter: React.Dispatch<React.SetStateAction<ProtectiveOrderState>>,
  instrumentMeta?: { pipSize?: number | null; priceIncrement?: number | null }
) => {
  return (value: number | '') => {
    setter((prev) => {
      if (value === '' || !isNumber(value)) return { ...prev, price: value }
      const formatted = formatPriceForInstrument(value, instrumentMeta)
      const normalized = typeof formatted === 'string' ? Number(formatted) : formatted
      return { ...prev, price: normalized }
    })
  }
}

export const TPSLModal: BaseModalType<CloseTradeModalProps> = (props) => {
  const {
    close,
    order,
    position,
    accountCode,
    accountCurrency,
    initialActiveType,
    instrumentMeta,
    onTPSLChange,
    cancelOrder,
    newOrder,
    changePendingOrderSlTp,
    replaceOrder,
  } = props
  const { t } = useTranslation()
  const { addSnackbar } = useSnackbar()
  const { formatNumber } = useFormatNumber()

  const symbol = position?.symbol ?? order?.instrument ?? ''
  const description = position?.description ?? order?.description ?? ''
  const side = position?.side ?? order?.side ?? null
  const sizeLots = position?.sizeLots ?? order?.sizeLots ?? null
  const entryPrice = position?.entryPrice ?? null
  const fractionDigits = getStepDecimals(instrumentMeta?.priceIncrement)

  const [isLoading, setIsLoading] = useState(false)
  const pendingCountRef = useRef(0)
  const isMountedRef = useRef(true)

  useEffect(() => {
    isMountedRef.current = true
    return () => {
      isMountedRef.current = false
    }
  }, [])

  const initialStopLoss = position?.stopLoss ?? order?.stopLoss ?? null
  const initialTakeProfit = position?.takeProfit ?? order?.takeProfit ?? null
  const hasInitialStopLoss = initialStopLoss != null
  const hasInitialTakeProfit = initialTakeProfit != null

  const [takeProfitState, setTakeProfitState] = useState<ProtectiveOrderState>(() =>
    createInitialOrderState(
      initialTakeProfit,
      initialActiveType === ProtectiveOrderTypes.takeProfit
    )
  )
  const [stopLossState, setStopLossState] = useState<ProtectiveOrderState>(() =>
    createInitialOrderState(initialStopLoss, initialActiveType === ProtectiveOrderTypes.stopLoss)
  )

  const instrument = position?.symbol ?? order?.instrument ?? undefined
  const baseSide = position?.side ?? order?.side ?? null
  const closingSide = invertSide(baseSide)
  const fixSide = sideMapping(closingSide ?? null)
  const takeProfitClientOrderId =
    position?.takeProfitClientOrderId ?? order?.takeProfitClientOrderId ?? null
  const stopLossClientOrderId =
    position?.stopLossClientOrderId ?? order?.stopLossClientOrderId ?? null
  const accountForCancel = position?.account ?? order?.account ?? null

  const priceContext = position ? 'position' : 'order'
  const currentPrice = useCurrentPriceForTrade(instrument, baseSide, priceContext)

  const targetPrice = order ? getOpenLegTargetPrice(order) : undefined
  const validationBasePrice = position ? currentPrice : targetPrice ?? currentPrice

  const pipSize = instrumentMeta?.pipSize

  const effectiveTpslBase: number | null =
    order != null && isNumber(targetPrice) ? targetPrice : currentPrice ?? null

  const isTakeProfitInputValid =
    !takeProfitState.isActive || (isNumber(takeProfitState.price) && takeProfitState.price > 0)
  const isStopLossInputValid =
    !stopLossState.isActive || (isNumber(stopLossState.price) && stopLossState.price > 0)

  const takeProfitValidation = getProtectiveOrderValidation({
    type: ProtectiveOrderTypes.takeProfit,
    price: takeProfitState.price,
    currentPrice: validationBasePrice,
    baseSide,
    isActive: takeProfitState.isActive,
  })

  const stopLossValidation = getProtectiveOrderValidation({
    type: ProtectiveOrderTypes.stopLoss,
    price: stopLossState.price,
    currentPrice: validationBasePrice,
    baseSide,
    isActive: stopLossState.isActive,
  })

  const shouldCancelTakeProfit = Boolean(
    hasInitialTakeProfit && takeProfitClientOrderId && !takeProfitState.isActive
  )
  const shouldCancelStopLoss = Boolean(
    hasInitialStopLoss && stopLossClientOrderId && !stopLossState.isActive
  )

  const hasAnyAction =
    takeProfitState.isActive ||
    stopLossState.isActive ||
    shouldCancelTakeProfit ||
    shouldCancelStopLoss

  const isConfirmDisabled =
    !hasAnyAction ||
    !isTakeProfitInputValid ||
    !isStopLossInputValid ||
    (takeProfitState.isActive && !!takeProfitValidation?.isError) ||
    (stopLossState.isActive && !!stopLossValidation?.isError) ||
    !accountCode

  const isOrder = !!order && !position
  const isPosition = !!position && !order

  const getProtectiveBaseParams = () => {
    const account = (isPosition ? position?.account : order?.account) ?? undefined
    const positionId = isPosition ? position?.tradeId : undefined

    if (!instrument || !fixSide || !account || (isPosition && !positionId)) {
      return null
    }

    return {
      account,
      symbol: instrument,
      side: fixSide,
      orderQty: 0,
      positionEffect: FIX_POSITION_EFFECT.CLOSE,
      timeInForce: FIX_TIME_IN_FORCE.DAY,
      ...(positionId ? { positionID: positionId } : {}),
      isTpsl: true,
    }
  }

  const handleConfirm = () => {
    const shouldCreateTakeProfit =
      takeProfitState.isActive && !hasInitialTakeProfit && isNumber(takeProfitState.price)
    const shouldCreateStopLoss =
      stopLossState.isActive && !hasInitialStopLoss && isNumber(stopLossState.price)

    const shouldUpdateTakeProfit =
      takeProfitState.isActive &&
      isNumber(takeProfitState.price) &&
      isNumber(initialTakeProfit) &&
      takeProfitState.price !== initialTakeProfit

    const shouldUpdateStopLoss =
      stopLossState.isActive &&
      isNumber(stopLossState.price) &&
      isNumber(initialStopLoss) &&
      stopLossState.price !== initialStopLoss

    const showError = () => addSnackbar.error({ message: t('errors.Something went wrong') })

    const startLoading = (count: number) => {
      pendingCountRef.current = count
      setIsLoading(true)
    }

    const handleSettled = () => {
      pendingCountRef.current -= 1
      if (pendingCountRef.current === 0) {
        if (isMountedRef.current) {
          setIsLoading(false)
          close()
        }
        onTPSLChange?.()
      }
    }

    const handleActionFailure = () => {
      showError()
      handleSettled()
    }

    //Creation first TP SL for pending orders — single ChangePendingOrderSlTpRequest via FIX

    const shouldUseOrderGroup = isOrder && (shouldCreateTakeProfit || shouldCreateStopLoss)

    if (shouldUseOrderGroup) {
      const symbol = order?.instrument
      const parentSide = sideMapping(order?.side ?? null)
      const account = order?.account ?? undefined
      const origClOrdID = order?.clientOrderId ?? undefined
      const protectionSide = sideMapping(closingSide ?? null)
      const legPrice = order?.legs?.[0]?.price
      const quantity = order?.legs?.[0]?.quantity

      if (
        !symbol ||
        !parentSide ||
        !account ||
        !origClOrdID ||
        !protectionSide ||
        !changePendingOrderSlTp ||
        quantity == null
      ) {
        showError()
        return
      }

      const ordType = mapOrderTypeToFixOrdType(order?.type as DxTradeOrderType | undefined)
      const expireDate = order?.expireDate ?? undefined
      const timeInForce = order?.expireDate
        ? FIX_TIME_IN_FORCE.GOOD_TILL_DATE
        : FIX_TIME_IN_FORCE.DAY

      const orders: NewOrderListOrderParams[] = [
        {
          account,
          symbol,
          side: parentSide,
          orderQty: quantity,
          ordType,
          positionEffect: FIX_POSITION_EFFECT.OPEN,
          timeInForce,
          expireDate,
          price: isNumber(legPrice) ? legPrice : undefined,
        },
      ]

      if (stopLossState.isActive && isNumber(stopLossState.price)) {
        orders.push({
          account,
          symbol,
          side: protectionSide,
          ordType: '3',
          positionEffect: FIX_POSITION_EFFECT.CLOSE,
          timeInForce,
          expireDate,
          price: stopLossState.price,
        })
      }

      if (takeProfitState.isActive && isNumber(takeProfitState.price)) {
        orders.push({
          account,
          symbol,
          side: protectionSide,
          ordType: '2',
          positionEffect: FIX_POSITION_EFFECT.CLOSE,
          timeInForce,
          expireDate,
          price: takeProfitState.price,
        })
      }

      startLoading(1)

      changePendingOrderSlTp(
        {
          origClOrdID,
          account,
          symbol,
          side: parentSide,
          contingencyType: FixContingencyTypes.IF_THEN,
          orders,
        },
        undefined,
        handleSettled
      )

      return
    }

    // positions and order updates
    const base = getProtectiveBaseParams()
    const actions: (() => void)[] = []

    if (shouldCreateTakeProfit) {
      actions.push(() => {
        if (!newOrder || !base) return handleActionFailure()
        newOrder(
          {
            ...base,
            ordType: getProtectiveOrdType('takeProfit'),
            price: takeProfitState.price as number,
          },
          undefined,
          handleSettled
        )
      })
    }

    if (shouldUpdateTakeProfit) {
      actions.push(() => {
        const origClOrdID = takeProfitClientOrderId ?? undefined
        if (!replaceOrder || !base || !origClOrdID) return handleActionFailure()
        replaceOrder(
          {
            ...base,
            origClOrdID,
            listId: accountForCancel ?? undefined,
            ordType: getProtectiveOrdType('takeProfit'),
            price: takeProfitState.price as number,
          },
          undefined,
          handleSettled
        )
      })
    }

    if (shouldCreateStopLoss) {
      actions.push(() => {
        if (!newOrder || !base) return handleActionFailure()
        newOrder(
          {
            ...base,
            ordType: getProtectiveOrdType('stopLoss'),
            price: stopLossState.price as number,
          },
          undefined,
          handleSettled
        )
      })
    }

    if (shouldUpdateStopLoss) {
      actions.push(() => {
        const origClOrdID = stopLossClientOrderId ?? undefined
        if (!replaceOrder || !base || !origClOrdID) return handleActionFailure()
        replaceOrder(
          {
            ...base,
            origClOrdID,
            listId: accountForCancel ?? undefined,
            ordType: getProtectiveOrdType('stopLoss'),
            price: stopLossState.price as number,
          },
          undefined,
          handleSettled
        )
      })
    }

    if (shouldCancelTakeProfit) {
      actions.push(() => {
        const origClOrdID = takeProfitClientOrderId ?? undefined
        if (!origClOrdID || !accountForCancel || !instrument || !fixSide || !cancelOrder) {
          return handleActionFailure()
        }
        cancelOrder({
          origClOrdID,
          account: accountForCancel,
          listId: accountForCancel,
          symbol: instrument,
          side: fixSide,
          isTpsl: true,
          onSettled: handleSettled,
        })
      })
    }

    if (shouldCancelStopLoss) {
      actions.push(() => {
        const origClOrdID = stopLossClientOrderId ?? undefined
        if (!origClOrdID || !accountForCancel || !instrument || !fixSide || !cancelOrder) {
          return handleActionFailure()
        }
        cancelOrder({
          origClOrdID,
          account: accountForCancel,
          listId: accountForCancel,
          symbol: instrument,
          side: fixSide,
          isTpsl: true,
          onSettled: handleSettled,
        })
      })
    }

    if (actions.length === 0) return

    startLoading(actions.length)
    actions.forEach((action) => action())
  }

  const handleActiveChange = (value: boolean, type: ProtectiveOrderType) => {
    const setter = type === ProtectiveOrderTypes.takeProfit ? setTakeProfitState : setStopLossState
    const hasInitial =
      type === ProtectiveOrderTypes.takeProfit ? hasInitialTakeProfit : hasInitialStopLoss
    const initialPrice =
      type === ProtectiveOrderTypes.takeProfit ? initialTakeProfit : initialStopLoss

    setter((prev) => {
      if (!value) return { ...prev, isActive: false }

      let next: number | '' = ''

      if (hasInitial && isNumber(initialPrice)) {
        next = initialPrice
      } else {
        const candidate = getDefaultProtectivePrice({
          type,
          isActive: true,
          prevPrice: '',
          basePrice: effectiveTpslBase,
          pipSize,
          baseSide,
        })

        if (isNumber(candidate)) {
          const formatted = formatPriceForInstrument(candidate, instrumentMeta)
          next = typeof formatted === 'string' ? Number(formatted) : candidate
        } else {
          next = ''
        }
      }

      return { ...prev, isActive: true, price: next }
    })
  }

  useEffect(() => {
    if (!initialActiveType) return
    handleActiveChange(true, initialActiveType)
  }, [])

  const handleTakeProfitPriceChange = createPriceChangeHandler(setTakeProfitState, instrumentMeta)
  const handleStopLossPriceChange = createPriceChangeHandler(setStopLossState, instrumentMeta)

  return (
    <Modal>
      <Modal.Header canClose>{t('Portfolio.TPSL')}</Modal.Header>
      <Modal.Body>
        <Box fullWidth gap='x16' flex direction='column'>
          <TradeCardRow
            className={cardStyles.singleTradeRow}
            sideLabel={formatSymbol(symbol, description)}
            rightPrimary={
              position ? (
                <TradePnL
                  trade={position}
                  accountCurrency={accountCurrency}
                  Typography={(typographyProps) => <Body1 medium {...typographyProps} />}
                />
              ) : undefined
            }
            caption={
              <Box flex justify='space-between' align='center'>
                <Box flex gap='x4'>
                  {renderSide(side, Caption1)}
                  {sizeLots != null && (
                    <Caption1 color='text-med-em'>
                      <SensitiveData>{sizeLots}</SensitiveData>{' '}
                      <span>
                        {entryPrice != null ? (
                          <>
                            {t('at')}{' '}
                            <SensitiveData>
                              {formatNumber(entryPrice, {
                                minDecimalPlaces: fractionDigits,
                                maxDecimalPlaces: fractionDigits,
                              })}
                            </SensitiveData>
                          </>
                        ) : (
                          ''
                        )}
                      </span>
                    </Caption1>
                  )}
                </Box>
                <LivePriceCell
                  trade={{ symbol, side, priceIncrement: instrumentMeta?.priceIncrement }}
                  priceType={order ? 'order' : 'position'}
                  TypographyComp={Caption1}
                  color='text-med-em'
                />
              </Box>
            }
          />

          <ProtectiveOrder
            title={t('Take profit')}
            type={ProtectiveOrderTypes.takeProfit}
            price={takeProfitState.price}
            isActive={takeProfitState.isActive}
            onActiveChange={(value: boolean) =>
              handleActiveChange(value, ProtectiveOrderTypes.takeProfit)
            }
            onPriceChange={handleTakeProfitPriceChange}
            position={position}
            order={order}
            accountCurrency={accountCurrency}
            instrumentCurrency={props.instrumentCurrency}
            instrumentMeta={props.instrumentMeta}
            currentPrice={effectiveTpslBase ?? undefined}
            basePrice={effectiveTpslBase}
            baseSide={baseSide}
          />
          <ProtectiveOrder
            title={t('Stop loss')}
            type={ProtectiveOrderTypes.stopLoss}
            price={stopLossState.price}
            isActive={stopLossState.isActive}
            onActiveChange={(value: boolean) =>
              handleActiveChange(value, ProtectiveOrderTypes.stopLoss)
            }
            onPriceChange={handleStopLossPriceChange}
            position={position}
            order={order}
            accountCurrency={accountCurrency}
            instrumentCurrency={props.instrumentCurrency}
            instrumentMeta={props.instrumentMeta}
            currentPrice={effectiveTpslBase ?? undefined}
            basePrice={effectiveTpslBase}
            baseSide={baseSide}
          />
        </Box>
      </Modal.Body>
      <Modal.Footer>
        <Modal.Footer.Primary
          disabled={isConfirmDisabled || isLoading}
          loading={isLoading}
          onClick={handleConfirm}
        >
          {t('Confirm')}
        </Modal.Footer.Primary>
      </Modal.Footer>
    </Modal>
  )
}
